import { Box, Typography, Link, Grid, Card, CardActionArea } from "@mui/material";
import { Security } from "@mui/icons-material";
import React from "react";
import { useManageKeys } from "../common/Providers/ManageKeysProvider/UseManageKeys";

interface Option {
  value: boolean;
  label: string;
}

interface BodhiProtectSelectorProps {
  title?: string;
  description?: string;
  bullets?: string[];
  options?: Option[];
  selectedValue: boolean;
  primaryColor?: string;
  onSelectYes: () => boolean | void;
  onSelectNo: () => boolean | void;
}


const defaultBullets = [
  "Accident, Illness & Pre-Existing conditions",
  "Transport disruptions",
  "Family or home emergency",
  "And many more!",
];

const defaultOptions = [
  { value: true, label: "Yes, protect my booking (Add $10)" },
  { value: false, label: "No thanks, I'll take the risk" },
];

const BodhiProtectSelector: React.FC<BodhiProtectSelectorProps> = ({
  title,
  description,
  bullets = defaultBullets,
  options = defaultOptions,
  selectedValue = true,
  primaryColor = "#0047C0",
  onSelectYes,
  onSelectNo,
}) => {
  const { getKey } = useManageKeys();
  
  const displayTitle = title || getKey('protect_your_booking_make_it_refundable', 'Protect your booking - Make it refundable!');
  const displayDescription = description || getKey('upgrade_your_booking_to_get_a_fast', "Upgrade your booking to get a fast, full refund if you can't attend due to one of the many covered reasons listed below.");

  const handleSelect = (value: boolean) => {
    if (value) onSelectYes();
    else onSelectNo();
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Box
          sx={{
            padding: "8px",
            borderRadius: "50%",
            backgroundColor: "#e3f2fd",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 1.5,
          }}
        >
          <Security sx={{ width: 20, height: 20 }} />
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {displayTitle}
        </Typography>
      </Box>

      <Typography variant="body2" sx={{ mb: 1.5, color: "#555" }}>
        {displayDescription}
      </Typography>

      <Box sx={{ mb: 2 }}>
        {bullets.map((item) => (
          <Box
            key={item}
            sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
          >
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                backgroundColor: "#4caf50",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 1,
                color: "#fff",
                fontSize: 14,
              }}
            >
              ✓
            </Box>
            <Typography variant="body2">{item}</Typography>
          </Box>
        ))}
      </Box>

      <Grid container spacing={2}>
        {options.map((opt, idx) => {
          const isSelected = selectedValue === opt.value;

          return (
            <Grid item key={idx + "_" + String(opt.value)} xs={12}>
              <Card
                onClick={() => handleSelect(opt.value)}
                sx={{
                  cursor: "pointer",
                  borderRadius: 1,
                  border: isSelected ? "2px solid" : "1px solid",
                  borderColor: isSelected ? primaryColor : "divider",
                  backgroundColor: isSelected ? primaryColor : "background.paper",
                  transition: "all 0.25s ease",
                  "&:hover": { borderColor: primaryColor },
                }}
              >
                <CardActionArea
                  sx={{
                    p: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="body1"
                    fontWeight={isSelected ? 600 : 500}
                    color={isSelected ? "white" : "text.primary"}
                  >
                    {opt.label}
                  </Typography>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Typography
        variant="caption"
        sx={{ display: "block", mt: 1.5, color: "#777" }}
        dangerouslySetInnerHTML={{
          __html: getKey(
            'by_selecting_yes_protect_my_booking_you_are_agreeing_to_all',
            'By selecting &quot;Yes, protect my booking&quot; you are agreeing to all <a href="/termsandconditions" target="_blank" style="text-decoration: underline;">Terms &amp; Conditions</a>, and you may receive up to a 100% refund on today&apos;s purchase. May not apply to pricing modifications made in the future.'
          )
        }}
      />
    </Box>
  );
};

export default BodhiProtectSelector;
